import React, { useEffect, useState } from 'react';
import { Company, PricingTier } from '@/types';
import { useCompanies, useCompanyMutations } from '@features/admin/data';
import { showAlertModal, showConfirmModal } from '@shared/components/common/AlertModal';
import { Pagination } from '@shared/components/common/Pagination';
import { formatUkDateTime } from '@/lib/dates';
import { normalizeErrorMessage } from '@/lib/errorUtils';
import { AlertCircle, Check, Trash2, Loader2, Mail } from 'lucide-react';

interface CompanyApprovalQueueProps {
  canEdit: boolean;
  onEdit: (company: Company) => void;
}

export const CompanyApprovalQueue: React.FC<CompanyApprovalQueueProps> = ({
  canEdit,
  onEdit: _onEdit
}) => {
  const companyMutations = useCompanyMutations();

  const [approvingCompany, setApprovingCompany] = useState<Company | null>(null);
  const [rejectingCompany, setRejectingCompany] = useState<Company | null>(null);
  const [pendingPage, setPendingPage] = useState(1);
  const [pendingPageSize, setPendingPageSize] = useState(25);
  const [isDeletingCompanyId, setIsDeletingCompanyId] = useState<string | null>(null);

  const [selectedTier, setSelectedTier] = useState<PricingTier>('bronze');
  const [selectedPaymentPref, setSelectedPaymentPref] = useState<'invoice' | 'card'>('invoice');
  const [selectedRequirePo, setSelectedRequirePo] = useState(false);

  // Reset approval form when switching to a different company
  useEffect(() => {
    setSelectedTier('bronze');
    setSelectedPaymentPref('invoice');
    setSelectedRequirePo(false);
  }, [approvingCompany?.id]);

  const handleDeleteCompany = async (company: Company) => {
    if (!canEdit) return;
    const confirmed = await showConfirmModal(
      `Delete "${company.name}" and all of its related records (bookings, forms, certificates, payments, and team users)? This cannot be undone.`,
      { title: 'Delete Company', confirmText: 'Delete', cancelText: 'Cancel' }
    );
    if (!confirmed) return;
    setIsDeletingCompanyId(company.id);
    try {
      await companyMutations.remove(company.id);
    } catch (e: unknown) {
      showAlertModal(normalizeErrorMessage(e, 'Failed to delete company.'));
    } finally {
      setIsDeletingCompanyId(null);
    }
  };

  return (
    <>
      <PendingList
        page={pendingPage}
        pageSize={pendingPageSize}
        isDeletingCompanyId={isDeletingCompanyId}
        canDelete={canEdit}
        onPageChange={setPendingPage}
        onPageSizeChange={(size) => {
          setPendingPageSize(size);
          setPendingPage(1);
        }}
        onApprove={(company) => setApprovingCompany(company)}
        onReject={(company) => setRejectingCompany(company)}
        onDelete={handleDeleteCompany}
      />

      {/* Approve Modal */}
      {approvingCompany && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full animate-fade-in">
            <h3 className="font-bold text-lg text-slate-900 mb-2">Approve Company</h3>
            <p className="text-slate-500 text-sm mb-4">
              Set up account details for {approvingCompany.name}.
            </p>
            <div className="mb-4 space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Pricing Tier</label>
                <select
                  className="w-full border rounded p-2 bg-white text-sm"
                  value={selectedTier}
                  onChange={(e) => setSelectedTier(e.target.value as PricingTier)}
                >
                  <option value="bronze">Bronze (Standard)</option>
                  <option value="silver">Silver (Discounted)</option>
                  <option value="gold">Gold (Premium)</option>
                  <option value="platinum">Platinum (VIP)</option>
                  <option value="diamond">Diamond (Partner)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Payment Type</label>
                <select
                  className="w-full border rounded p-2 bg-white text-sm"
                  value={selectedPaymentPref}
                  onChange={(e) => {
                    const val = e.target.value as 'invoice' | 'card';
                    setSelectedPaymentPref(val);
                    if (val === 'card') setSelectedRequirePo(false);
                  }}
                >
                  <option value="invoice">Invoice Later (No upfront payment)</option>
                  <option value="card">Card Payment (Charge per booking)</option>
                </select>
              </div>
              {selectedPaymentPref === 'invoice' && (
                <div className="bg-blue-50/50 p-3 rounded-lg border border-blue-100 mt-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-teal-600 rounded"
                      checked={selectedRequirePo}
                      onChange={(e) => setSelectedRequirePo(e.target.checked)}
                    />
                    <span className="text-xs font-bold text-blue-900">Mandatory PO Number</span>
                  </label>
                  <p className="text-[10px] text-blue-600 mt-1 ml-6">
                    Forcing a PO ensures cleaner reconciliation for invoice-based clients.
                  </p>
                </div>
              )}
            </div>
            <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 mb-6">
              <div className="text-sm text-slate-700 flex items-center gap-1">
                <Mail className="w-3 h-3 text-slate-500" />
                "Account Approved" Email will be sent automatically.
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setApprovingCompany(null)}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  await companyMutations.approve(
                    approvingCompany.id,
                    selectedTier,
                    selectedPaymentPref,
                    selectedRequirePo
                  );
                  setApprovingCompany(null);
                }}
                className="bg-teal-600 text-white px-4 py-2 rounded hover:bg-teal-700 font-medium"
              >
                Confirm Approval
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {rejectingCompany && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full animate-fade-in">
            <div className="flex items-center gap-2 text-red-600 mb-2">
              <AlertCircle className="w-6 h-6" />
              <h3 className="font-bold text-lg">Reject Application</h3>
            </div>
            <p className="text-slate-500 text-sm mb-4">
              Are you sure you want to reject the application from{' '}
              <strong>{rejectingCompany.name}</strong>? This cannot be undone.
            </p>
            <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 mb-6">
              <div className="text-sm text-slate-700 flex items-center gap-1">
                <Mail className="w-3 h-3 text-slate-500" />
                "Application Rejected" Email will be sent automatically.
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setRejectingCompany(null)}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  await companyMutations.reject(rejectingCompany.id);
                  setRejectingCompany(null);
                }}
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 font-medium"
              >
                Reject Application
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Internal list component — not exported
interface PendingListProps {
  page: number;
  pageSize: number;
  isDeletingCompanyId: string | null;
  canDelete: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onApprove: (company: Company) => void;
  onReject: (company: Company) => void;
  onDelete: (company: Company) => void;
}

const PendingList: React.FC<PendingListProps> = ({
  page,
  pageSize,
  isDeletingCompanyId,
  canDelete,
  onPageChange,
  onPageSizeChange,
  onApprove,
  onReject,
  onDelete
}) => {
  const result = useCompanies({ page, pageSize, status: 'pending' });
  const totalCount = Number(result.totalCount || 0);
  const items = result.items || [];

  if (items.length === 0 && page === 1) return null;

  return (
    <div className="bg-orange-50 border border-orange-200 rounded-xl p-6">
      <h3 className="text-lg font-bold text-orange-800 flex items-center gap-2 mb-4">
        <AlertCircle className="w-5 h-5" /> Pending Approvals ({totalCount})
      </h3>
      <div className="space-y-3">
        {items.length === 0 ? (
          <div className="bg-white p-6 rounded-lg border border-orange-100 text-center text-slate-500 italic">
            No pending approvals on this page.
          </div>
        ) : (
          items.map((company) => (
            <div
              key={company.id}
              className="bg-white p-4 rounded-lg shadow-sm border border-orange-100 flex justify-between items-center"
            >
              <div>
                <h4 className="font-bold text-slate-900">{company.name}</h4>
                <p className="text-sm text-slate-500">
                  Contact: {company.contactPerson} ({company.email})
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  Requested: {formatUkDateTime(company.createdAt, '—')}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => onReject(company)}
                  disabled={isDeletingCompanyId === company.id}
                  className="text-red-600 px-4 py-2 text-sm font-medium hover:bg-red-50 rounded disabled:opacity-40"
                >
                  Reject
                </button>
                <button
                  onClick={() => onApprove(company)}
                  disabled={isDeletingCompanyId === company.id}
                  className="bg-teal-600 text-white px-4 py-2 text-sm font-medium hover:bg-teal-700 rounded flex items-center gap-2 disabled:opacity-40"
                >
                  <Check className="w-4 h-4" /> Approve
                </button>
                {canDelete && (
                  <button
                    onClick={() => onDelete(company)}
                    disabled={isDeletingCompanyId === company.id}
                    className="text-red-700 border border-red-200 px-4 py-2 text-sm font-medium hover:bg-red-50 rounded flex items-center gap-2 disabled:opacity-40"
                  >
                    {isDeletingCompanyId === company.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
      <Pagination
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </div>
  );
};
